// src/contexts/ClientContext.tsx
// BRIDGE FIX: frontend expects 'clients' table with agency_id
// DB has: users + tenant_users with tenantId
// Maps between them — interface IDENTICAL to original

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAgency } from './AgencyContext';

// ─── Types (unchanged from original) ─────────────────────────────────────────

export interface Client {
  id: string;
  agency_id: string;       // mapped from tenantId
  first_name: string;      // mapped from fullName split
  last_name: string;
  email: string;
  phone?: string;
  date_of_birth?: string;
  country_of_citizenship?: string;
  created_at: string;
  updated_at: string;
}

interface ClientContextType {
  clients: Client[];
  currentClient: Client | null;
  loading: boolean;
  error: string | null;
  fetchClients: () => Promise<void>;
  fetchClientById: (id: string) => Promise<Client | null>;
  createClient: (data: Partial<Client>) => Promise<Client | null>;
  updateClient: (id: string, data: Partial<Client>) => Promise<Client | null>;
  deleteClient: (id: string) => Promise<boolean>;
  refreshClients: () => Promise<void>;
  setCurrentClient: (client: Client | null) => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// ─── Helper: map DB user + tenant_user metadata → Client shape ────────────────
function mapToClient(user: any, tenantId: string, meta?: any): Client {
  // fullName may be "John Smith" or just "John"
  const nameParts = (user.full_name ?? user.fullName ?? '').trim().split(/\s+/);
  const first_name = nameParts[0] ?? '';
  const last_name = nameParts.slice(1).join(' ') || '';

  return {
    id: user.id,
    agency_id: tenantId,
    first_name,
    last_name,
    email: user.email ?? '',
    phone: meta?.phone ?? user.phone,
    date_of_birth: meta?.dateOfBirth ?? meta?.date_of_birth,
    country_of_citizenship: meta?.nationality ?? meta?.countryOfCitizenship,
    created_at: user.createdAt ?? user.created_at ?? new Date().toISOString(),
    updated_at: user.updatedAt ?? user.updated_at ?? new Date().toISOString(),
  };
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentAgency } = useAgency();
  const [clients, setClients] = useState<Client[]>([]);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch all clients for this agency ────────────────────────────────────
  const fetchClients = useCallback(async () => {
    if (!currentAgency) { setClients([]); setLoading(false); return; }

    setLoading(true);
    setError(null);

    try {
      // Get CLIENT user IDs from agency_members (primary source)
      const { data: amRows } = await supabase
        .from('agency_members')
        .select('userId, joinedAt')
        .eq('tenantId', currentAgency.id)
        .eq('role', 'CLIENT');

      const allIds = (amRows ?? []).map((r: any) => r.userId).filter(Boolean);

      if (!allIds.length) { setClients([]); setLoading(false); return; }

      // Fetch user profiles from public.users
      const { data: userProfiles, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name, created_at, updated_at, tier, status')
        .in('id', allIds);

      if (userError) throw userError;
      const userIds_final = allIds;

      const metaMap: Record<string,any> = Object.fromEntries(
        (amRows ?? []).map((r: any) => [r.userId, {}])
      );

      const mapped = (userProfiles ?? []).map((u: any) =>
        mapToClient(u, currentAgency.id, metaMap[u.id])
      );

      // Sort by created_at desc
      mapped.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setClients(mapped);

    } catch (err: any) {
      console.error('ClientContext fetchClients error:', err?.message ?? err);
      setError('Unable to load clients. Please try again.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [currentAgency]);

  // ── Fetch single client ───────────────────────────────────────────────────
  const fetchClientById = useCallback(async (id: string): Promise<Client | null> => {
    if (!currentAgency) return null;
    try {
      const { data: userProfile } = await supabase
        .from('users')
        .select('id, email, full_name, created_at, updated_at, tier, status')
        .eq('id', id)
        .single();

      if (!userProfile) return null;

      const { data: tu, error: tuError } = await supabase
        .from('agency_members')
        .select('metadata')
        .eq('userId', id)
        .eq('tenantId', currentAgency.id)
        .maybeSingle();

      if (tuError) console.error('fetchClientById metadata fetch error:', tuError.message);

      return mapToClient(userProfile, currentAgency.id, tu?.metadata ?? {});
    } catch { return null; }
  }, [currentAgency]);

  // ── Create client ─────────────────────────────────────────────────────────
  const createClient = useCallback(async (data: Partial<Client>): Promise<Client | null> => {
    if (!currentAgency) { setError('No agency selected'); return null; }
    try {
      const now = new Date().toISOString();
      const fullName = `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim();
      const userId = crypto.randomUUID(); // valid UUID for public.users

      // Step 1: Create in public.users
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .upsert({
          id: userId,
          email: data.email!,
          full_name: fullName,
          tier: 'basic',
          is_onboarding_completed: true,
          status: 'ACTIVE',
          created_at: now,
          updated_at: now,
        }, { onConflict: 'email', ignoreDuplicates: false })
        .select()
        .single();

      if (userError) throw userError;

      // Step 2: Create in visaguide.users (required for FK)
      const { error: vuError } = await supabase
        .from('agency_users')
        .upsert({
          id: userId,
          email: data.email!,
          fullName: fullName,
          status: 'ACTIVE',
          createdAt: now,
          updatedAt: now,
        } as any)
        .then(() => ({ error: null }))
        .catch(() => ({ error: null }));

      // Step 3: Insert into visaguide.tenant_users via direct SQL. Now
      // actually sends phone/date_of_birth/nationality (2026-09-12) --
      // ClientCreation.fixed.tsx (the real routed creation form) has had
      // live input fields for these since before this fix, but
      // create_client_member() never accepted them, so real user-entered
      // values were silently discarded. Previously this call's error was
      // unconditionally swallowed (`.then(() => ({error:null})).catch(() =>
      // ({error:null}))` reported no error on EITHER path); now surfaced.
      const { error: tuError } = await supabase.rpc('create_client_member', {
        p_tenant_id: currentAgency.id,
        p_user_id: userId,
        p_full_name: fullName,
        p_email: data.email!,
        p_now: now,
        p_phone: data.phone ?? null,
        p_date_of_birth: data.date_of_birth ?? null,
        p_nationality: data.country_of_citizenship ?? null,
      });
      if (tuError) console.error('create_client_member RPC error:', tuError.message);

      const client = mapToClient(newUser, currentAgency.id, {
        phone: data.phone,
        dateOfBirth: data.date_of_birth,
        nationality: data.country_of_citizenship,
      });

      setClients(prev => [client, ...prev]);
      return client;

    } catch (err: any) {
      console.error('CreateClient error:', err?.message ?? err);
      setError('Unable to create client. Please try again.');
      return null;
    }
  }, [currentAgency]);

  // ── Update client ─────────────────────────────────────────────────────────
  const updateClient = useCallback(async (id: string, data: Partial<Client>): Promise<Client | null> => {
    if (!currentAgency) return null;
    try {
      const now = new Date().toISOString();
      const fullName = data.first_name || data.last_name
        ? `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim()
        : undefined;

      if (fullName || data.email) {
        await supabase.from('agency_users')
          .update({ ...(fullName ? { fullName } : {}), ...(data.email ? { email: data.email } : {}), updatedAt: now })
          .eq('id', id);
      }

      // Update metadata in tenant_users
      const { data: existing, error: existingError } = await supabase.from('agency_members')
        .select('metadata').eq('userId', id).eq('tenantId', currentAgency.id).single();
      if (existingError) console.error('updateClient metadata read error:', existingError.message);

      const { error: updateError } = await supabase.from('agency_members').update({
        metadata: {
          ...(existing?.metadata ?? {}),
          ...(data.phone !== undefined ? { phone: data.phone } : {}),
          ...(data.date_of_birth !== undefined ? { dateOfBirth: data.date_of_birth } : {}),
          ...(data.country_of_citizenship !== undefined ? { nationality: data.country_of_citizenship } : {}),
        },
        updatedAt: now,
      }).eq('userId', id).eq('tenantId', currentAgency.id);
      if (updateError) console.error('updateClient metadata write error:', updateError.message);

      const updated = await fetchClientById(id);
      if (updated) {
        setClients(prev => prev.map(c => c.id === id ? updated : c));
        if (currentClient?.id === id) setCurrentClient(updated);
      }
      return updated;

    } catch (err: any) {
      console.error('UpdateClient error:', err.message);
      return null;
    }
  }, [currentAgency, currentClient, fetchClientById]);

  // ── Delete client (remove from tenant) ───────────────────────────────────
  const deleteClient = useCallback(async (id: string): Promise<boolean> => {
    if (!currentAgency) return false;
    try {
      const { error } = await supabase.from('agency_members')
        .update({ role: 'VIEWER' }) // Soft remove — change role instead of delete
        .eq('userId', id)
        .eq('tenantId', currentAgency.id);
      if (error) throw error;
      setClients(prev => prev.filter(c => c.id !== id));
      if (currentClient?.id === id) setCurrentClient(null);
      return true;
    } catch { return false; }
  }, [currentAgency, currentClient]);

  const refreshClients = useCallback(async () => { await fetchClients(); }, [fetchClients]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  return (
    <ClientContext.Provider value={{
      clients, currentClient, loading, error,
      fetchClients, fetchClientById, createClient,
      updateClient, deleteClient, refreshClients, setCurrentClient,
    }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClient must be used within ClientProvider');
  return ctx;
};
